import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Users, CheckCircle2, Copy, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  matched_new: { label: "Will import", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
  will_update: { label: "Will update", cls: "text-blue-700 bg-blue-50 border-blue-200", Icon: RefreshCw },
  duplicate: { label: "Already exists", cls: "text-slate-600 bg-slate-50 border-slate-200", Icon: Copy },
  unmatched_account: { label: "No matching account", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: AlertTriangle },
  invalid: { label: "Missing data", cls: "text-rose-700 bg-rose-50 border-rose-200", Icon: XCircle },
};

export default function ContactImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const inputRef = useRef();

  const reset = () => { setFile(null); setPreview(null); setFilter("all"); };

  const run = async (mode) => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("options_json", JSON.stringify({ mode, tag: "Imported" }));
      const r = await api.post("/contacts/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (mode === "apply") {
        const sum = r.data.summary || {};
        const parts = [];
        if (sum.matched_new) parts.push(`${sum.matched_new} added`);
        if (sum.will_update) parts.push(`${sum.will_update} updated`);
        toast.success(parts.length ? `Import complete — ${parts.join(" · ")}` : "Import complete");
        onImported?.();
        reset();
        onClose?.();
      } else {
        setPreview(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const s = preview?.summary;
  const rows = (preview?.results || []).filter((r) => filter === "all" || r.status === filter);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" /> Import contacts</DialogTitle>
          <DialogDescription>
            Upload a spreadsheet (CSV/XLSX) with <b>Account Name</b>, <b>Email</b> and name columns — plus any phone
            columns you want to add (<i>Phone, Mobile, Direct Dial, HQ Phone</i>). Each row is matched to an existing
            account by name, then to an existing contact by email (or first+last). <b>If the contact already exists,
            blank fields like phone number are filled in from the sheet — nothing is overwritten.</b> New contacts are
            created when there's no match. Rows with no matching account are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="border-2 border-dashed border-slate-200 rounded-lg p-5 text-center cursor-pointer hover:border-blue-300"
            onClick={() => inputRef.current?.click()}
            data-testid="contact-import-dropzone"
          >
            <Upload className="h-6 w-6 text-slate-400 mx-auto mb-1.5" />
            <div className="text-sm text-slate-700 font-medium">{file ? file.name : "Choose a file to upload"}</div>
            <div className="text-xs text-slate-400 mt-0.5">CSV or Excel · matched to accounts by name</div>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.xlsm"
              className="hidden"
              data-testid="contact-import-file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            />
          </div>

          {!preview && (
            <Button onClick={() => run("dry_run")} disabled={!file || busy} className="w-full" data-testid="contact-import-preview-btn">
              {busy ? "Analysing…" : "Preview import"}
            </Button>
          )}

          {preview && (
            <>
              <div className="grid grid-cols-5 gap-2" data-testid="contact-import-summary">
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <button
                    key={k}
                    onClick={() => setFilter(filter === k ? "all" : k)}
                    className={`rounded-md border p-2 text-left transition-all ${m.cls} ${filter === k ? "ring-2 ring-offset-1 ring-blue-400" : ""}`}
                    data-testid={`contact-import-count-${k}`}
                  >
                    <div className="text-lg font-bold">{s?.[k] ?? 0}</div>
                    <div className="text-[10px] font-medium leading-tight">{m.label}</div>
                  </button>
                ))}
              </div>

              <div className="text-[11px] text-slate-500 font-mono">
                {preview.total_rows} rows · account col: “{preview.detected_columns?.account}” · {filter === "all" ? "showing all" : `filtered: ${STATUS_META[filter]?.label}`}
              </div>

              {/* Detected-column hint — surfaces whether phone / mobile /
                  direct-dial / HQ were picked up so users spot a missing
                  mapping before they commit the import. */}
              <div className="flex flex-wrap gap-1.5" data-testid="contact-import-detected-cols">
                {["email", "first_name", "last_name", "full_name", "phone", "mobile", "direct_dial", "hq_phone", "job_title"].map((k) => {
                  const detected = preview.detected_columns?.[k];
                  const label = k.replace(/_/g, " ");
                  return (
                    <span
                      key={k}
                      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-mono ${
                        detected
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-slate-50 text-slate-400"
                      }`}
                      title={detected ? `Mapped to “${detected}”` : "Not detected in your file"}
                    >
                      {label}
                      {detected && <span className="text-slate-500">· {detected}</span>}
                    </span>
                  );
                })}
              </div>

              <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto divide-y divide-slate-100" data-testid="contact-import-rows">
                {rows.slice(0, 400).map((r) => {
                  const m = STATUS_META[r.status] || {};
                  const Icon = m.Icon || CheckCircle2;
                  // Compact phone preview — only shown when at least one
                  // phone field was imported for this row, so the row
                  // doesn't get crowded for email-only rows.
                  const phones = [r.phone, r.mobile, r.direct_dial, r.hq_phone].filter(Boolean);
                  const fieldsToUpdate = r.fields_to_update || [];
                  return (
                    <div key={r.row} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <Icon className={`h-3.5 w-3.5 shrink-0 ${m.cls?.split(" ")[0]}`} />
                      <span className="font-medium text-slate-800 truncate w-40">{r.contact || "—"}</span>
                      <span className="text-slate-400 truncate flex-1">{r.account_name || "—"}</span>
                      {fieldsToUpdate.length > 0 && (
                        <span
                          className="font-mono text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 truncate max-w-[240px]"
                          title={`Will fill blanks: ${fieldsToUpdate.join(", ")}`}
                        >
                          +{fieldsToUpdate.join(", ").replace(/_/g, " ")}
                        </span>
                      )}
                      {phones.length > 0 && fieldsToUpdate.length === 0 && (
                        <span className="font-mono text-[10px] text-slate-500 truncate max-w-[180px]" title={phones.join(" · ")}>
                          {phones[0]}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.cls}`}>{m.label}</span>
                    </div>
                  );
                })}
                {rows.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">No rows in this category.</div>}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={reset} disabled={busy}>Choose another file</Button>
                <Button
                  onClick={() => run("apply")}
                  disabled={busy || !((s?.matched_new || 0) + (s?.will_update || 0))}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="contact-import-apply-btn"
                >
                  {busy
                    ? "Importing…"
                    : (() => {
                        const add = s?.matched_new || 0;
                        const upd = s?.will_update || 0;
                        const bits = [];
                        if (add) bits.push(`add ${add}`);
                        if (upd) bits.push(`update ${upd}`);
                        return bits.length ? `Apply — ${bits.join(" · ")}` : "Nothing to apply";
                      })()
                  }
                </Button>
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
